/**
 * BatchPushModal Component
 *
 * Modal for pushing a template to multiple agents at once.
 * Shows agent selection with checkboxes and push progress.
 *
 * @module components/claude-templates/BatchPushModal
 */

import { useState, useEffect, memo, useCallback } from 'react';
import { Upload, Loader2, X, Bot, CheckCircle, AlertCircle, Check } from 'lucide-react';

const API_BASE = '/api';

/**
 * Push template to a single agent
 */
async function pushToAgent(agentId, templateId) {
  const res = await fetch(`${API_BASE}/claude-templates/agents/${agentId}/claude-md`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ templateId })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to push template');
  }
  return res.json();
}

/**
 * AgentCheckboxItem component
 */
const AgentCheckboxItem = memo(function AgentCheckboxItem({
  agent,
  isSelected,
  onToggle,
  pushStatus,
  isDisabled
}) {
  const statusIcon = {
    pending: null,
    pushing: <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />,
    success: <CheckCircle className="w-4 h-4 text-green-400" />,
    error: <AlertCircle className="w-4 h-4 text-red-400" />
  }[pushStatus || 'pending'];

  return (
    <label
      className={`
        flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
        ${isSelected
          ? 'bg-indigo-600/20 border-indigo-500/50'
          : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
        }
        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggle(agent.id)}
        disabled={isDisabled}
        className="w-4 h-4 rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-900"
      />
      <Bot className="w-5 h-5 text-gray-400" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-200 truncate">
          {agent.displayName || agent.name}
        </div>
        {agent.host?.hostname && (
          <div className="text-xs text-gray-500 truncate">
            {agent.host.hostname}
          </div>
        )}
      </div>
      {statusIcon && (
        <div className="flex-shrink-0">
          {statusIcon}
        </div>
      )}
    </label>
  );
});

/**
 * BatchPushModal Component
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {object} props.template - Template to push
 * @param {Array} props.agents - List of available agents
 * @param {function} props.onClose - Close callback
 * @param {function} props.onComplete - Callback when all pushes complete
 */
const BatchPushModal = memo(function BatchPushModal({
  isOpen,
  template,
  agents,
  onClose,
  onComplete
}) {
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [pushStatus, setPushStatus] = useState({}); // { agentId: 'pending' | 'pushing' | 'success' | 'error' }
  const [isPushing, setIsPushing] = useState(false);
  const [results, setResults] = useState({ success: 0, failed: 0 });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAgentIds(new Set());
      setPushStatus({});
      setIsPushing(false);
      setResults({ success: 0, failed: 0 });
    }
  }, [isOpen]);

  const handleToggleAgent = useCallback((agentId) => {
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedAgentIds.size === agents.length) {
      setSelectedAgentIds(new Set());
    } else {
      setSelectedAgentIds(new Set(agents.map(a => a.id)));
    }
  }, [agents, selectedAgentIds.size]);

  const handlePush = useCallback(async () => {
    if (selectedAgentIds.size === 0 || !template) return;

    setIsPushing(true);
    const agentIds = Array.from(selectedAgentIds);
    let successCount = 0;
    let failedCount = 0;

    // Push to each agent sequentially
    for (const agentId of agentIds) {
      setPushStatus(prev => ({ ...prev, [agentId]: 'pushing' }));

      try {
        await pushToAgent(agentId, template.id);
        setPushStatus(prev => ({ ...prev, [agentId]: 'success' }));
        successCount++;
      } catch {
        setPushStatus(prev => ({ ...prev, [agentId]: 'error' }));
        failedCount++;
      }
    }

    setResults({ success: successCount, failed: failedCount });
    setIsPushing(false);

    // Call onComplete if all succeeded
    if (failedCount === 0 && onComplete) {
      onComplete(successCount);
    }
  }, [selectedAgentIds, template, onComplete]);

  const isAllSelected = selectedAgentIds.size === agents.length;
  const hasSelection = selectedAgentIds.size > 0;
  const isComplete = results.success > 0 || results.failed > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Batch Push</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPushing}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Template Info */}
        <div className="px-6 py-3 bg-gray-800/50 border-b border-gray-700 flex-shrink-0">
          <span className="text-sm text-gray-400">
            Template: <span className="text-white font-medium">{template?.name}</span>
          </span>
        </div>

        {/* Agent List */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Select All */}
          <div className="mb-3">
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={isPushing}
              className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50"
            >
              {isAllSelected ? 'Deselect All' : 'Select All'} ({agents.length} agents)
            </button>
          </div>

          {/* Agent Checkboxes */}
          <div className="space-y-2">
            {agents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No agents available</p>
              </div>
            ) : (
              agents.map((agent) => (
                <AgentCheckboxItem
                  key={agent.id}
                  agent={agent}
                  isSelected={selectedAgentIds.has(agent.id)}
                  onToggle={handleToggleAgent}
                  pushStatus={pushStatus[agent.id]}
                  isDisabled={isPushing}
                />
              ))
            )}
          </div>
        </div>

        {/* Results Summary (shown after push) */}
        {isComplete && (
          <div className="px-6 py-3 border-t border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-4 text-sm">
              {results.success > 0 && (
                <span className="text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {results.success} succeeded
                </span>
              )}
              {results.failed > 0 && (
                <span className="text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {results.failed} failed
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-500">
            {hasSelection
              ? `${selectedAgentIds.size} agent${selectedAgentIds.size > 1 ? 's' : ''} selected`
              : 'Select agents to push to'}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPushing}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {isComplete ? 'Close' : 'Cancel'}
            </button>
            {!isComplete && (
              <button
                type="button"
                onClick={handlePush}
                disabled={isPushing || !hasSelection}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {isPushing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Push to {selectedAgentIds.size || '...'} Agent{selectedAgentIds.size !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default BatchPushModal;
